import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import ItemRow from "./ItemRow";
import ItemForm from "./ItemForm";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { ChevronDown, ChevronRight, Folder, FolderOpen, MoreHorizontal, Pencil, CopyPlus, Eye, EyeOff, Trash2, ArrowUp, ArrowDown, Plus } from "lucide-react";

// A collapsible sub-folder ("Learn It" / "Study It" / "Apply It" / custom) inside a module.
export default function SectionBlock({ section, module: mod, sectionIndex, isInstructor, open, onToggle, onChanged, onError, onGoToExams, onGoToProjects, materials, exams, projects, skillTags, tagFilter }) {
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(section.title);

  const act = async (fn) => {
    try { await fn(); onChanged(); }
    catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggleHidden = () => act(() => api.patch(`/modules/${mod.id}/sections/${section.id}`, { hidden: !section.hidden }));
  const duplicate = () => act(() => api.post(`/modules/${mod.id}/sections/${section.id}/duplicate`));
  const remove = () => {
    if (!window.confirm(`Delete section "${section.title}" and its ${section.items.length} item(s)? (Linked materials/exams/projects are NOT deleted.)`)) return;
    act(() => api.delete(`/modules/${mod.id}/sections/${section.id}`));
  };
  const moveSection = (dir) => {
    const ids = mod.sections.map((s) => s.id);
    const j = sectionIndex + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[sectionIndex], ids[j]] = [ids[j], ids[sectionIndex]];
    act(() => api.post(`/modules/${mod.id}/sections/reorder`, { section_ids: ids }));
  };
  const saveRename = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    act(() => api.patch(`/modules/${mod.id}/sections/${section.id}`, { title: name.trim() }));
    setRenaming(false);
  };

  const items = tagFilter ? section.items.filter((i) => (i.tags || []).includes(tagFilter)) : section.items;

  return (
    <div data-testid={`section-${section.id}`} className={`rounded-lg border-2 border-[var(--gold)]/40 ${section.hidden ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between gap-2 px-3 py-2.5">
        <button data-testid={`section-toggle-${section.id}`} onClick={onToggle} className="flex items-center gap-2 min-w-0 flex-1 text-left hover:text-[var(--gold)] transition-colors">
          {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
          {open ? <FolderOpen className="w-4 h-4 text-[var(--gold)] shrink-0" /> : <Folder className="w-4 h-4 text-[var(--gold)] shrink-0" />}
          {renaming ? (
            <form onSubmit={saveRename} className="flex gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
              <input data-testid={`section-rename-input-${section.id}`} autoFocus value={name} onChange={(e) => setName(e.target.value)} className="nb-input text-sm py-1" />
              <button data-testid={`section-rename-save-${section.id}`} className="nb-btn nb-btn-yellow text-xs">Save</button>
            </form>
          ) : (
            <span className="font-display font-bold truncate">
              {section.title}
              <span className="ml-2 text-xs font-sans font-normal text-[var(--muted-text)]">{section.items.length} item{section.items.length === 1 ? "" : "s"}</span>
              {section.hidden && isInstructor && <span data-testid={`section-hidden-badge-${section.id}`} className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--muted-text)]/25 text-[var(--gold-soft)]">Hidden</span>}
            </span>
          )}
        </button>
        {isInstructor && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`section-menu-${section.id}`} className="nb-btn nb-btn-white text-sm px-2"><MoreHorizontal className="w-4 h-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[75]">
              <DropdownMenuItem data-testid={`section-edit-${section.id}`} onClick={() => { setName(section.title); setRenaming(true); }}><Pencil className="w-4 h-4 mr-2" /> Edit (rename)</DropdownMenuItem>
              <DropdownMenuItem data-testid={`section-duplicate-${section.id}`} onClick={duplicate}><CopyPlus className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
              <DropdownMenuItem data-testid={`section-toggle-hidden-${section.id}`} onClick={toggleHidden}>
                {section.hidden ? <><Eye className="w-4 h-4 mr-2" /> Show</> : <><EyeOff className="w-4 h-4 mr-2" /> Hide</>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={sectionIndex === 0} onClick={() => moveSection(-1)}><ArrowUp className="w-4 h-4 mr-2" /> Move up</DropdownMenuItem>
              <DropdownMenuItem disabled={sectionIndex === mod.sections.length - 1} onClick={() => moveSection(1)}><ArrowDown className="w-4 h-4 mr-2" /> Move down</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={`section-delete-${section.id}`} onClick={remove} className="text-red-600 focus:text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {open && (
        <div className="px-3 pb-3">
          {items.length > 0 ? (
            <ul className="divide-y-2 divide-[var(--gold)]/20 border-t-2 border-[var(--gold)]/20">
              {items.map((item, idx) => (
                <ItemRow
                  key={item.id} item={item} moduleId={mod.id} sectionId={section.id}
                  sections={mod.sections} isInstructor={isInstructor}
                  index={section.items.indexOf(item)} total={section.items.length}
                  onChanged={onChanged} onError={onError}
                  onGoToExams={onGoToExams} onGoToProjects={onGoToProjects}
                  materials={materials} exams={exams} projects={projects} skillTags={skillTags}
                />
              ))}
            </ul>
          ) : (
            <p className="text-xs text-[var(--muted-text)] py-2 border-t-2 border-[var(--gold)]/20">
              {tagFilter ? "No items match this skill filter." : "No items in this section yet."}
            </p>
          )}
          {isInstructor && (
            <button data-testid={`add-item-btn-${section.id}`} onClick={() => setAdding(true)} className="mt-2 nb-btn nb-btn-white text-xs flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add item
            </button>
          )}
        </div>
      )}

      {adding && (
        <ItemForm mode="add" moduleId={mod.id} sectionId={section.id} materials={materials} exams={exams} projects={projects} skillTags={skillTags} onClose={() => setAdding(false)} onSaved={onChanged} />
      )}
    </div>
  );
}
